import React, { useEffect, useState } from 'react';
import { useBusiness, useTitle } from '../Hooks';
import { Button, Checkbox, Field, Groupper, Header, Input, Message, Placeholder, Toast } from 'react-frontier';
import { useParams } from 'react-router-dom';
import { UserBusinessAccess, BusinessUser, SetLoading } from '../Classes';
import { Card, UserAccessConfig, Modal } from '../components';
import { bindClick, bindFormChange } from '@tempu/components/Util';
import { firstBy } from 'thenby';
import BusinessAccess from '../BusinessAccess';
import API from '../API';
import Validator from '@tempu/components/Validator';
import moment from 'moment';

interface EditUserAccess extends UserBusinessAccess{
	user_id: number,
	first_name: string,
	last_name: string,
	email: string,
}
interface BusinessAccessData {
	business_id: number;
	admin: boolean;
	access: number[];
}

enum ShownModal{
	ADD = 1,
	EDIT = 2,
	REMOVE = 3,
}

var BusinessUsers = ()=>{
	var params = useParams<{ id: string }>();
	var business_id = parseInt(params?.id);
	var { setTitle, setAction } = useTitle();
	var { hasAccess, business_name, } = useBusiness(business_id);
	var [loadError, setLoadError] = useState<string>(null);
	var [userList, setUserList] = useState<BusinessUser[]>(null);
	var [shownModal, setShownModal] = useState<ShownModal>(null);
	var [modalLoadError, setModalLoadError] = useState<string>(null);
	var [modalPrompts, setModalPrompts] = useState<string[]>(null);
	var [addAdmin, setAddAdmin] = useState<boolean>(false);
	var [addEmail, setAddEmail] = useState<string>('');
	var [addAccess, setAddAccess] = useState<number[]>([]);
	var [editUser, setEditUser] = useState<EditUserAccess>(null);

	useEffect(()=>{
		setTitle('Usuarios');
		if(!params || !business_id || Number.isNaN(business_id)) return;

		API.getBusinessUsers(business_id).then(res=>{
			if(res.error) return setLoadError(res.message);
			setUserList(res.data);
		}).catch(e=>{
			return setLoadError('Hubo un error inesperado cargando los usuarios (LCL-1)');
		});
	}, []);

	useEffect(()=>{
		if(hasAccess(BusinessAccess.ADMIN.ADD_USER)){
			setAction(
				<Button text='Agregar usuario' color='black' iconName='plus-circle' onClick={showAddModal} />
			)
		}
	}, []);

	var showAddModal = ()=>{
		setAddEmail('');
		setAddAccess([]);
		setAddAdmin(false);
		setModalPrompts(null);
		setShownModal(ShownModal.ADD);
	}

	var showEditModal = (user_id: number)=>{
		return ()=>{
			var user = userList.find(a=>a.user_id===user_id);
			if(!user) return;
			setShownModal(ShownModal.EDIT);
			if(editUser?.user_id!==user_id){
				setModalLoadError(null);
				setEditUser(null);
				API.getBusinessUser(business_id, user_id).then(res=>{
					if(res.error) return setModalLoadError(res.message);
					setEditUser({
						user_id: user.user_id,
						first_name: user.first_name,
						last_name: user.last_name,
						email: user.email,
						...res.data,
					})
				}).catch(err=>{
					setModalLoadError('Hubo un error cargando los accesos del usuario. (LCL-1)');
				});
			}
		}
	}

	var addUser = (setLoading: SetLoading)=>{
		var filteredAccess = addAdmin ? BusinessAccess.ACCESS_ARRAY.flatMap(cat =>
         (cat.access as any[]).filter(acc => acc.reserved && addAccess.includes(acc.id)).map(acc => acc.id)
      ) : addAccess;

		var { valid, prompts } = Validator({
			email: addEmail,
			access: addAdmin ? [1] : addAccess
		}, {
			email: ['email'],
			access: [{ rule: 'minLength', params: [1], prompt: 'Favor de darle por lo menos 1 acceso al usuario' }],
		});

		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.addBusinessUser(business_id, addEmail, addAdmin, filteredAccess).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setUserList([
				...userList.filter(a=>a.user_id!==res.data.user_id),
				res.data
			]);
			setShownModal(null);
			Toast.success('Se ha agregado el usuario al negocio.');
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado agregando el usuario (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendEditUser = (setLoading: SetLoading)=>{
		var filteredAccess = editUser.admin ? BusinessAccess.ACCESS_ARRAY.flatMap(cat =>
         (cat.access as any[]).filter(acc => acc.reserved && editUser.access.includes(acc.id)).map(acc => acc.id)
      ) : editUser.access;

		var { valid, prompts } = Validator({
			access: editUser.admin ? [1] : editUser.access
		}, {
			access: [{ rule: 'minLength', params: [1], prompt: 'Favor de darle por lo menos 1 acceso al usuario' }],
		});
		setModalPrompts(prompts);
		if(!valid) return;

		setLoading(true);
		API.editBusinessUser(business_id, editUser.user_id, editUser.admin, filteredAccess).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setShownModal(null);
			setEditUser(null);
			var bu = [...userList];
			var ix = bu.findIndex(a=>a.user_id===editUser.user_id);
			if(ix===-1) return;
			bu[ix].admin = editUser.admin;
			setEditUser({
				...editUser,
				access: filteredAccess
			});
			setUserList(bu);
			Toast.success('Se ha editado el usuario');
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado editando el usuario (LCL-1)']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var sendRemoveUser = (setLoading: SetLoading)=>{
		if(!editUser) return;
		setLoading(true);
		API.removeBusinessUser(business_id, editUser.user_id).then(res=>{
			if(res.error) return setModalPrompts([res.message]);
			setUserList(userList.filter(a=>a.user_id!==editUser.user_id));
			Toast.success(`Se ha eliminado el acceso de ${editUser.first_name} ${editUser.last_name} del negocio.`);
			setShownModal(null);
		}).catch(err=>{
			setModalPrompts(['Hubo un error inesperado eliminando el acceso del usuario.']);
		}).finally(()=>{
			setLoading(false);
		})
	}

	var onEditChange = bindFormChange(editUser, setEditUser);
	
	return <div className="tp business-users-screen">
		{loadError ? (
			<Header text='Error' subtext={loadError} />
		) : userList ? (
			userList.sort(firstBy('admin', 'desc').thenBy('date_added', 'asc')).map(a=>(
				<Card className='user' image={a.image} imageFallbackIcon='image' key={`BSNUSR-${a.user_id}`} onClick={showEditModal(a.user_id)}>
					<div className="left">
						<div className="meta small">Usuario #{a.user_id}</div>
						<div className="name">{a.first_name} {a.last_name}</div>
						<div className="meta">{a.email}</div>
						<div className="meta">Desde {moment.unix(a.date_added).format('DD/MMM/YY')}</div>
					</div>
					{!!a.admin && (
						<div className="right">
							<i className="user shield icon"></i>
							<div className="text">Admin</div>
						</div>
					)}
				</Card>
			))
		) : (new Array(3).fill('TEMPU').map((a,i)=>(
			<Card placeholder placeholderImage className='user' key={`CRDPLH-${i}`}>
				<div className="left">
					<Placeholder />
					<Placeholder />
					<Placeholder />
				</div>
			</Card>
		)))}
		{hasAccess(BusinessAccess.ADMIN.EDIT_USER_ACCESS) && <>
			<Modal open={shownModal === ShownModal.ADD} onClose={bindClick(setShownModal)} size="tiny">
				<Modal.Header>Agregar usuario</Modal.Header>
				<Modal.Content>
					<Input label="Correo electrónico" value={addEmail} onChange={setAddEmail} />
					{hasAccess(BusinessAccess.ADMIN._ID) && <Groupper>
						<Checkbox
							label="Usuario administrador"
							checked={addAdmin}
							onChange={setAddAdmin}
						/>
					</Groupper>}
					<>
						{addAdmin && (
							<Message
								type="info"
								text={
									BusinessAccess.ACCESS_ARRAY.some(cat => 
										(cat.access as any[]).some(acc => acc.reserved)
									) 
									? `Este usuario es administrador, pero requiere permisos adicionales para acceder a funciones restringidas.`
									: `Este usuario tendrá acceso completo para realizar cambios de ${business_name} y sus empleos.`
								}
								style={{ marginTop: 15 }}
							/>
						)}

						{(!addAdmin || BusinessAccess.ACCESS_ARRAY.some(cat => 
							(cat.access as any[]).some(acc => acc.reserved)
						)) && (
							<UserAccessConfig
								accesss={addAccess}
								onAccessChange={setAddAccess}
								style={{ marginTop: addAdmin ? 10 : 20 }}
								admin={addAdmin}
								hasAdminAccess={hasAccess(BusinessAccess.ADMIN._ID)}
							/>
						)}
					</>
					<Message list={modalPrompts} type="error" />
				</Modal.Content>
				<Modal.Actions>
					<Button text="Cerrar" basic onClick={bindClick(setShownModal)} />
					<Button text="Guardar" color="black" onClick={addUser} />
				</Modal.Actions>
			</Modal>
			<Modal open={shownModal===ShownModal.EDIT} onClose={bindClick(setShownModal)} size='tiny'>
				<Modal.Header>Editar usuario</Modal.Header>
				<Modal.Content>
					{modalLoadError ? (
							<Header text='Error' subtext={modalLoadError} iconName='frown-open' style={{ padding: 0, margin: 0 }} />
					) : !editUser ? (
							<Header loading text='Cargando usuario' style={{ padding: 20 }} />
					) : <>
							<Field amount={2}>
								<Input label='Nombre' value={`${editUser.first_name} ${editUser.last_name}`} readonly />
								<Input label='Correo electrónico' value={editUser.email} readonly />
							</Field>
							{hasAccess(BusinessAccess.ADMIN._ID) && <Groupper>
								<Checkbox label='Usuario administrador' checked={editUser.admin} onChange={onEditChange('admin')} />
							</Groupper>}
							<UserAccessConfig 
								accesss={editUser.access} 
								onAccessChange={onEditChange('access')} 
								style={{ marginTop: 20 }} 
								admin={editUser.admin}
								hasAdminAccess={hasAccess(BusinessAccess.ADMIN._ID)}
							/>				
							{editUser.admin && (
								<Message 
									type={'info'} 
									text={`Este usuario tendrá acceso completo para realizar cambios de ${business_name} y sus empleos.`} 
									style={{ marginTop: 15, marginBottom: 15 }} 
								/>
							)}
							
							<Button basic color='red' text='Eliminar usuario' style={{ display: 'block', margin: 'auto', width: 200 }} onClick={()=>setShownModal(ShownModal.REMOVE)} />
							<Message list={modalPrompts} type='error' />
					</>}
				</Modal.Content>
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					{!!editUser && (
							<Button text='Guardar' color='black' onClick={sendEditUser} />
					)}
				</Modal.Actions>
			</Modal>
		</>}
		{hasAccess(BusinessAccess.ADMIN.REMOVE_USER_ACCESS) && (
			<Modal open={shownModal===ShownModal.REMOVE} onClose={bindClick(setShownModal)} size='mini'>
				<Modal.Header>Eliminar usuario</Modal.Header>
				{!!editUser && (
					<Modal.Content>
						<Header text='¿Eliminar usuario de negocio?' />
						<Message type='info' centered>
							Esto eliminará los accesos al negocio de <b>{editUser.first_name} {editUser.last_name}</b>.
						</Message>
						<Message list={modalPrompts} type='error' />
					</Modal.Content>
				)}
				<Modal.Actions>
					<Button text='Cerrar' basic onClick={bindClick(setShownModal)} />
					{!!editUser && (
						<Button text='Eliminar' color='red' onClick={sendRemoveUser} />
					)}
				</Modal.Actions>
			</Modal>
		)}
	</div>
}

export default BusinessUsers;